import { db } from '../firebase';
import { collection, addDoc, getDocs, query, where, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';

export interface OpenForConnection {
  id?: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhoto?: string;
  photoPosition?: { x: number; y: number }; // Photo position for proper display
  userRegion?: string; // Legacy single region (display only)
  userRegions?: string[]; // Array of regions for filtering
  userCompany?: string;
  userBio?: string;
  userPostcode?: string;
  userIndustry?: string[] | string;
  userSectors?: string[];
  subscriptionTier: 'free' | 'premium' | 'enterprise';
  createdAt: Date;
  expiresAt: Date;
  isActive: boolean;
}

class OpenForConnectionsService {
  private collectionName = 'openForConnections';

  /**
   * Stel je open voor nieuwe connecties
   */
  async setOpenForConnections(
    userId: string,
    userData: {
      userName: string;
      userEmail: string;
      userPhoto?: string;
      photoPosition?: { x: number; y: number };
      userRegion?: string; // Legacy single region (for display)
      userRegions?: string[]; // Array of regions (for filtering)
      userCompany?: string;
      userBio?: string;
      subscriptionTier: 'free' | 'premium' | 'enterprise';
    }
  ): Promise<string> {
    try {
      // If userRegions or photoPosition not provided, fetch from userProfile
      let finalUserData = { ...userData };
      if (!finalUserData.userRegions || finalUserData.userRegions.length === 0 || !finalUserData.photoPosition) {
        try {
          const userDoc = await getDoc(doc(db, 'userProfiles', userId));
          if (userDoc.exists()) {
            const profileData = userDoc.data();
            if (!finalUserData.userRegions || finalUserData.userRegions.length === 0) {
              finalUserData.userRegions = profileData.selectedRegions || [];
            }
            if (!finalUserData.photoPosition && profileData.photoPosition) {
              finalUserData.photoPosition = profileData.photoPosition;
            }
          }
        } catch (err) {
          console.warn('Could not load user profile data:', err);
          finalUserData.userRegions = finalUserData.userRegions || [];
        }
      }

      // Check if user is already open for connections
      const existingQuery = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('isActive', '==', true)
      );
      const existingSnapshot = await getDocs(existingQuery);
      
      if (!existingSnapshot.empty) {
        // Update existing entry
        const existingDoc = existingSnapshot.docs[0];
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 7); // 7 days from now
        
        await updateDoc(doc(db, this.collectionName, existingDoc.id), {
          ...finalUserData,
          expiresAt,
          updatedAt: new Date()
        });
        
        return existingDoc.id;
      } else {
        // Create new entry
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 7); // 7 days from now
        
        const newEntry: Omit<OpenForConnection, 'id'> = {
          userId,
          ...finalUserData,
          createdAt: new Date(),
          expiresAt,
          isActive: true
        };
        
        const docRef = await addDoc(collection(db, this.collectionName), newEntry);
        return docRef.id;
      }
    } catch (error) {
      console.error('Error setting open for connections:', error);
      throw error;
    }
  }

  /**
   * Haal alle gebruikers op die open staan voor connecties (free tier eerst)
   */
  async getOpenForConnections(
    excludeUserId?: string,
    limit: number = 10
  ): Promise<OpenForConnection[]> {
    try {
      // First, get the current user's regions if userId provided
      let userRegions: string[] = [];
      if (excludeUserId) {
        try {
          const userDoc = await getDoc(doc(db, 'userProfiles', excludeUserId));
          if (userDoc.exists()) {
            userRegions = userDoc.data().selectedRegions || [];
          }
        } catch (err) {
          console.warn('Could not load user regions:', err);
        }
      }

      const q = query(
        collection(db, this.collectionName),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const now = new Date();
      const connections = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          expiresAt: data.expiresAt?.toDate ? data.expiresAt.toDate() : data.expiresAt
        };
      }) as OpenForConnection[];
      
      // ✅ FIX: Filter out expired entries + auto-cleanup
      const activeConnections: OpenForConnection[] = [];
      const expiredDocIds: string[] = [];
      
      connections.forEach(c => {
        const expiresAt = c.expiresAt instanceof Date ? c.expiresAt : new Date(c.expiresAt);
        if (expiresAt > now) {
          activeConnections.push(c);
        } else {
          expiredDocIds.push(c.id!);
          console.log(`🗑️ Expired entry: ${c.userName} (expired ${expiresAt.toLocaleDateString('nl-NL')})`);
        }
      });
      
      // Auto-cleanup expired entries in background
      if (expiredDocIds.length > 0) {
        console.log(`🗑️ Cleaning up ${expiredDocIds.length} expired openForConnections entries...`);
        expiredDocIds.forEach(async (docId) => {
          try {
            await updateDoc(doc(db, this.collectionName, docId), { isActive: false, expiredAt: now });
          } catch (err) {
            console.warn('Could not deactivate expired entry:', docId, err);
          }
        });
      }
      
      // Filter out current user
      let filteredConnections = excludeUserId 
        ? activeConnections.filter(c => c.userId !== excludeUserId)
        : activeConnections;
      
      console.log('🔍 getOpenForConnections debug:', {
        totalConnections: connections.length,
        afterExcludeCurrent: filteredConnections.length,
        userRegions,
        connectionsWithRegions: filteredConnections.filter(c => c.userRegions && c.userRegions.length > 0).map(c => ({
          userId: c.userId,
          userName: c.userName,
          userRegions: c.userRegions
        })),
        connectionsWithoutRegions: filteredConnections.filter(c => !c.userRegions || c.userRegions.length === 0).map(c => ({
          userId: c.userId,
          userName: c.userName
        }))
      });
      
      // Filter by region overlap (only if user has regions set)
      // ✅ FIX: Show users without regions as fallback, and prioritize users with matching regions
      if (userRegions.length > 0) {
        const beforeFilter = filteredConnections.length;
        
        // Separate connections into: matching regions, no regions, other regions
        const withMatchingRegions: OpenForConnection[] = [];
        const withoutRegions: OpenForConnection[] = [];
        const withOtherRegions: OpenForConnection[] = [];
        
        filteredConnections.forEach(connection => {
          if (!connection.userRegions || connection.userRegions.length === 0) {
            // User has no regions - show them as fallback
            withoutRegions.push(connection);
            console.log(`✅ Including ${connection.userName} (${connection.userId}): no regions set (fallback)`);
          } else {
            // Check if there's any overlap between user's regions and connection's regions
            const hasOverlap = connection.userRegions.some(region => userRegions.includes(region));
            if (hasOverlap) {
              withMatchingRegions.push(connection);
              console.log(`✅ Including ${connection.userName} (${connection.userId}): matching regions`);
            } else {
              withOtherRegions.push(connection);
              console.log(`❌ Filtered out ${connection.userName} (${connection.userId}): no region overlap. Their regions: ${connection.userRegions.join(', ')}, Your regions: ${userRegions.join(', ')}`);
            }
          }
        });
        
        // Combine: matching regions first, then users without regions (as fallback)
        filteredConnections = [...withMatchingRegions, ...withoutRegions];
        console.log(`📊 Region filter: ${beforeFilter} → ${filteredConnections.length} connections (${withMatchingRegions.length} matching, ${withoutRegions.length} no regions, ${withOtherRegions.length} other regions filtered)`);
      } else {
        // If current user has no regions, show all users (no filtering)
        console.log('📊 No regions selected by current user - showing all open connections');
      }
      
      // Sort by creation date (newest first) - all tiers treated equally
      const sortedConnections = filteredConnections.sort((a, b) => {
        const timeA = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime();
        const timeB = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime();
        return timeB - timeA;
      });
      
      const limitedConnections = sortedConnections.slice(0, limit);
      
      // ✅ Enrich with latest userProfile data (photoPosition, displayName, etc.)
      const enrichedConnections = await Promise.all(
        limitedConnections.map(async (connection) => {
          try {
            const userDoc = await getDoc(doc(db, 'userProfiles', connection.userId));
            if (userDoc.exists()) {
              const profileData = userDoc.data();
              return {
                ...connection,
                photoPosition: profileData.photoPosition || connection.photoPosition,
                userPhoto: profileData.photoURL || connection.userPhoto,
                userName: profileData.displayName || connection.userName,
                userCompany: profileData.company || connection.userCompany
              };
            }
          } catch (err) {
            console.warn(`Could not enrich profile for ${connection.userId}:`, err);
          }
          return connection;
        })
      );
      
      return enrichedConnections;
    } catch (error) {
      console.error('Error getting open for connections:', error);
      return [];
    }
  }

  /**
   * Sluit je af voor nieuwe connecties
   */
  async closeForConnections(userId: string): Promise<void> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const batch = snapshot.docs.map(doc => 
        updateDoc(doc.ref, { isActive: false, closedAt: new Date() })
      );
      
      await Promise.all(batch);
    } catch (error) {
      console.error('Error closing for connections:', error);
      throw error;
    }
  }

  /**
   * Check if user is currently open for connections
   */
  async isOpenForConnections(userId: string): Promise<boolean> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking open for connections:', error);
      return false;
    }
  }

  /**
   * Clean up expired entries
   */
  async cleanupExpiredEntries(): Promise<void> {
    try {
      const now = new Date();
      const q = query(
        collection(db, this.collectionName),
        where('expiresAt', '<', now)
      );
      
      const snapshot = await getDocs(q);
      const batch = snapshot.docs.map(doc => 
        updateDoc(doc.ref, { isActive: false, expiredAt: now })
      );
      
      await Promise.all(batch);
    } catch (error) {
      console.error('Error cleaning up expired entries:', error);
    }
  }
}

const openForConnectionsService = new OpenForConnectionsService();
export default openForConnectionsService;
